import {
  memo,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';

import { getTitle } from '@gn-audio/jss-core';

import { getElementPolygon, Point, pointWithinPolygon, Polygon } from '../../../utils';
import { JabraFocusGroup, JabraFocusItem } from '../../JabraFocus';
import { DISPLAY_VERSIONS } from '../MegaMenuConstants';
import { MajorLink, MajorLinksCategory } from '../megaMenuTypes';
import { useHeaderNavigation } from '../providers';
import { DesktopMenuItem } from './DesktopMenuItem';

import styles from '../MegaMenu.module.css';

type MajorLinksCategoryProps = {
  category: MajorLinksCategory;
  detailsPanel?: HTMLElement | null;
};

export const DesktopMajorLinksCategory = memo(function DesktopMajorLinksCategory({
  category,
  detailsPanel,
}: MajorLinksCategoryProps): ReactElement | null {
  const { activeMajorLink, onMajorCategoryHover } = useHeaderNavigation();
  const blindSpot = useRef<Polygon | undefined>(undefined);
  const activateMenuItemTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const majorLinks = category.MajorLinks;
  const majorSublinks = useMemo(
    () => (activeMajorLink ? activeMajorLink?.MajorSublinks : []),
    [activeMajorLink]
  );
  const subLinksRef = useRef(null);
  const majorLinksRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const activateLink = useCallback(
    (majorLink?: MajorLink) => {
      if (majorLink) {
        onMajorCategoryHover?.(majorLink);
      }
    },
    [onMajorCategoryHover]
  );

  useEffect(() => {
    if (majorLinks && majorLinks.length) {
      activateLink(majorLinks[0]);
    }
  }, [activateLink, majorLinks]);

  const handleMouseOver = (
    event: ReactMouseEvent<HTMLElement, MouseEvent>,
    majorLink?: MajorLink
  ) => {
    const { clientX, clientY } = event;
    const mousePosition: Point = [clientX, clientY];

    clearTimeout(activateMenuItemTimeout.current);

    const isMouseInsideBlindTriangle =
      blindSpot.current && pointWithinPolygon(mousePosition, blindSpot.current);

    updateBlindSpot(event, true);
    if (isMouseInsideBlindTriangle) {
      activateMenuItemTimeout.current = setTimeout(() => activateLink(majorLink), 500);
      return;
    }
    activateLink(majorLink);
  };

  const handleOnFocusLink = (majorLink: MajorLink) => {
    activateLink(majorLink);
  };

  const handleSubLinkMouseOver = (e: ReactMouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    clearTimeout(activateMenuItemTimeout.current);
  };

  const updateBlindSpot = (e: ReactMouseEvent<HTMLElement, MouseEvent>, isActive: boolean) => {
    if (!isActive) return;

    const { clientX, clientY } = e;
    const entryPoint: Point = [clientX, clientY];

    const nextPanel = subLinksRef.current ?? detailsPanel;
    if (!nextPanel || !majorLinksRef.current) return;

    blindSpot.current = getElementPolygon(entryPoint, majorLinksRef.current, nextPanel);
  };

  const handleMouseOut = (e: ReactMouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    clearTimeout(activateMenuItemTimeout.current);
  };

  const categoryName = category.Label?.value;

  const handleMouseOutOfLinks = (e: ReactMouseEvent<HTMLElement, MouseEvent>) => {
    if (containerRef.current === e.currentTarget) {
      blindSpot.current = undefined;
    }
  };

  const filteredMajorSublinks = useMemo(
    () => majorSublinks?.filter((item) => !!item.Link && !!getTitle(item)),
    [majorSublinks]
  );

  return (
    <div onMouseLeave={handleMouseOutOfLinks} ref={containerRef}>
      <div className="flex min-w-fit lg:pr-4">
        <div className="mr-4 hidden min-w-fit lg:block" ref={majorLinksRef}>
          <span className="flex flex-1 text-gray-400">{categoryName}:</span>
          <JabraFocusGroup orientation="vertical" id="majorlinks">
            <ul>
              {majorLinks?.map((majorLink, index) => {
                const majorLinkTitle = getTitle(majorLink) || '';
                const majorLinkField = majorLink.Link;
                const isActive =
                  !!activeMajorLink && getTitle(majorLink) === getTitle(activeMajorLink);

                return (
                  <li
                    key={`${index}-${majorLinkTitle}`}
                    aria-current={isActive ? 'true' : undefined}
                    onFocus={() => handleOnFocusLink(majorLink)}
                  >
                    <JabraFocusItem asChild>
                      <div
                        className={classNames(styles.desktopSubmenuItemContainer, 'font-semibold')}
                      >
                        <DesktopMenuItem
                          title={majorLinkTitle}
                          onMouseOver={(e) => handleMouseOver(e, majorLink)}
                          onMouseOut={handleMouseOut}
                          linkField={majorLinkField}
                          isActive={isActive}
                          additionalClass={classNames(
                            `yellow-underline ${isActive && 'active'}`,
                            styles.desktopSubmenuItem,
                            DISPLAY_VERSIONS.DESKTOP
                          )}
                        />
                      </div>
                    </JabraFocusItem>
                  </li>
                );
              })}
            </ul>
          </JabraFocusGroup>
        </div>

        {!!filteredMajorSublinks?.length && (
          <JabraFocusGroup orientation="vertical" id="sublinks">
            <div ref={subLinksRef} className="ml-2 mr-3 hidden w-[170px] flex-1 pt-6 lg:block">
              <ul className="flex-1">
                {filteredMajorSublinks.map((majorSublink, index) => {
                  const majorSublinkField = majorSublink.Link;
                  const majorSublinkTitle = getTitle(majorSublink);
                  return (
                    majorSublinkField &&
                    majorSublinkTitle && (
                      <li
                        className={styles.desktopSubmenuItemContainer}
                        key={`${index}-${majorSublinkTitle}`}
                      >
                        <JabraFocusItem>
                          <DesktopMenuItem
                            title={majorSublinkTitle}
                            onMouseOver={handleSubLinkMouseOver}
                            onMouseOut={handleMouseOut}
                            linkField={majorSublinkField}
                            additionalClass={classNames(
                              'yellow-underline',
                              styles.desktopSubmenuItem,
                              DISPLAY_VERSIONS.DESKTOP
                            )}
                          />
                        </JabraFocusItem>
                      </li>
                    )
                  );
                })}
              </ul>
            </div>
          </JabraFocusGroup>
        )}
      </div>
    </div>
  );
});
